import React, { useEffect, useState } from 'react';
import { PushService } from '../services/PushService';
import { useAuth } from '../contexts/AuthContext';
import { Switch, Banner } from './ui';

export const PushNotificationToggle: React.FC = () => {
    const { loadUser } = useAuth();
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [permission, setPermission] = useState<NotificationPermission>('default');

    useEffect(() => {
        checkSubscription();
    }, []);

    const checkSubscription = async () => {
        if (!PushService.isSupported()) {
            setIsLoading(false);
            return;
        }

        try {
            setPermission(Notification.permission);
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);

            // If there's a local subscription, silently re-sync it with the backend.
            // This handles the case where pushsubscriptionchange fired in the background
            // (e.g. FCM token rotation) while the auth session was expired, causing the
            // backend registration to have been lost.
            if (subscription) {
                PushService.syncSubscriptionWithBackend(subscription).catch(() => {
                    // Non-critical: silently ignore sync errors
                });
            }
        } catch (err) {
            console.error('Error checking subscription', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = async () => {
        setIsLoading(true);
        setError(null);

        try {
            if (isSubscribed) {
                await PushService.unsubscribeFromPush();
                setIsSubscribed(false);
                await loadUser(); // Update user device list
            } else {
                const perm = await PushService.requestPermission();
                setPermission(perm);
                if (perm !== 'granted') {
                    setError('Berechtigung für Benachrichtigungen wurde verweigert.');
                    setIsLoading(false);
                    return;
                }

                await PushService.subscribeToPush();
                setIsSubscribed(true);
                await loadUser(); // Refresh user to show new device
                // Send test notification so user sees it works immediately
                try { await PushService.sendTestNotification(); } catch { /* non-critical */ }
            }
        } catch (err: any) {
            setError(err.message || 'Fehler bei der Push-Konfiguration');
        } finally {
            setIsLoading(false);
        }
    };

    if (!PushService.isSupported()) {
        return (
            <p className="text-sm italic text-danger-text">
                Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <Switch
                checked={isSubscribed}
                onCheckedChange={() => handleToggle()}
                disabled={isLoading}
                label="Push auf diesem Gerät aktivieren"
                description={isSubscribed ? 'Dieses Gerät ist für Push-Benachrichtigungen registriert.' : 'Noch nicht registriert.'}
            />

            {permission === 'denied' && (
                <Banner tone="warning">Benachrichtigungen sind in deinen Browser-Einstellungen blockiert.</Banner>
            )}

            {error && <Banner tone="danger">{error}</Banner>}
        </div>
    );
};
